/**
 * MUIBoxTable
 *
 * Lightweight generic table built on MUI Table components.
 * - Accepts typed columns with optional render(row) for cell customization.
 * - Shows ellipsis with tooltip for long text values via CustomTooltip.
 * - Special-cases a column with key "remove" to render a clickable remove icon
 *   that does not trigger the row click handler.
 *
 * Props
 * - columns: { key, label, render? }[]
 * - data: any[]
 * - noResultText?: fallback message when no rows
 * - onRowClick?: optional row click handler
 */
import React, { useState, useRef, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Popover,
} from "@mui/material";
import "./mui-box-table.scss";
import { MUIBoxTableProps } from "./mui-table-props";
import CustomTooltip from "./custom-tooltip";
import RemoveIcon from "../../../assets/icons/remove-icon.svg";
import TableHeaderArrow from "../../../assets/icons/sortIcon.svg";

const TooltipCell = ({
  content,
  style,
}: {
  content: React.ReactNode;
  style: React.CSSProperties;
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element) {
      if (element.scrollWidth > element.clientWidth) {
        setIsOverflowed(true);
      } else {
        setIsOverflowed(false);
      }
    }
  }, [content]);

  const span = (
    <span
      ref={ref}
      className="cell-ellipsis"
      style={{
        ...style,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
      }}
    >
      {content}
    </span>
  );

  return isOverflowed ? (
    <CustomTooltip title={content as string} arrow placement="top-start">
      {span}
    </CustomTooltip>
  ) : (
    span
  );
};

const UNSORTABLE_COLUMNS = new Set(["edit", "remove"]);

const MUIBoxTable = <T extends Record<string, any>>({
  columns,
  data,
  noResultText,
  onRowClick,
  onRemoveClick,
}: MUIBoxTableProps<T>) => {
  // Sort menu state
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [sortMenuColumn, setSortMenuColumn] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [blockRowClick, setBlockRowClick] = useState(false);

  // Open sort menu
  const handleOpenSortMenu = (
    event: React.MouseEvent<HTMLElement>,
    columnKey: string,
  ) => {
    event.stopPropagation();
    setSortMenuAnchor(event.currentTarget);
    setSortMenuColumn(columnKey);
  };

  // Close sort menu
  const handleCloseSortMenu = () => {
    setSortMenuAnchor(null);
    setSortMenuColumn("");
    setBlockRowClick(true);
    setTimeout(() => setBlockRowClick(false), 100);
  };

  // Handle sort selection from menu
  const handleSortSelection = (
    direction: "asc" | "desc" | null,
    event?: React.MouseEvent,
  ) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    }

    handleCloseSortMenu();

    setTimeout(() => {
      if (direction === null) {
        setSortConfig(null);
      } else {
        setSortConfig({ key: sortMenuColumn, direction });
      }
    }, 100);
  };

  const numericPattern = /^-?\d+(?:\.\d+)?$/;

  const extractComparableValue = (value: any): string | number | null => {// NOSONAR
    if (value == null) {
      return null;
    }

    if (typeof value === "number") {
      return value;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed === "" ? null : trimmed;
    }

    if (Array.isArray(value)) {
      const parts = value
        .map((item) => extractComparableValue(item))
        .filter((part): part is string | number => part !== null);

      if (parts.length === 0) {
        return null;
      }

      const allNumbers = parts.every((part) => typeof part === "number");
      if (allNumbers) {
        return Number(parts[0]);
      }

      return parts.map(String).join(", ");
    }

    if (React.isValidElement(value)) {
      const props = (value.props || {}) as Record<string, unknown>;
      const explicitSortValue =
        props["data-sort-value"] ??
        props["dataSortValue"] ??
        props["sortValue"];

      if (
        typeof explicitSortValue === "string" ||
        typeof explicitSortValue === "number"
      ) {
        return explicitSortValue;
      }

      if (Object.hasOwn(props, "children")) {
        return extractComparableValue(props["children"]);
      }

      return null;
    }

    if (typeof value === "object") {
      const candidateKeys = [
        "sortValue",
        "label",
        "value",
        "name",
        "title",
        "text",
      ];
      for (const key of candidateKeys) {
        if (!Object.hasOwn(value, key)) continue;
        const candidate = (value as Record<string, unknown>)[key];
        const extracted = extractComparableValue(candidate);
        if (extracted !== null) {
          return extracted;
        }
      }
    }

    return null;
  };

  const getSortValue = React.useCallback(
    (row: T, columnKey: string): string | number | null => {
      const column = columns.find((col) => col.key === columnKey);

      if (column?.render) {
        const rendered = column.render(row);
        const extractedFromRender = extractComparableValue(rendered);
        if (extractedFromRender !== null) {
          return extractedFromRender;
        }
      }

      const raw = row[columnKey as keyof T];
      const extractedFromRaw = extractComparableValue(raw);
      if (extractedFromRaw !== null) {
        return extractedFromRaw;
      }

      return null;
    },
    [columns],
  );

  // Sort data based on sortConfig
  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {// NOSONAR
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const isANumeric =
        typeof aValue === "number" ||
        (typeof aValue === "string" && numericPattern.test(aValue));
      const isBNumeric =
        typeof bValue === "number" ||
        (typeof bValue === "string" && numericPattern.test(bValue));

      if (isANumeric && isBNumeric) {
        const aNumber = Number(aValue);
        const bNumber = Number(bValue);
        return sortConfig.direction === "asc"
          ? aNumber - bNumber
          : bNumber - aNumber;
      }

      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();

      if (sortConfig.direction === "asc") {
        if (aString < bString) return -1;
        if (aString > bString) return 1;
        return 0;
      } else {
        if (aString > bString) return -1;
        if (aString < bString) return 1;
        return 0;
      }
    });
  }, [data, getSortValue, sortConfig]);

  return (
    <>
      <TableContainer component={Paper} className="box-table-container">
        <Table className="box-table">
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const isSortable =
                  !UNSORTABLE_COLUMNS.has(column.key) &&
                  column.sortable !== false;
                return (
                  <TableCell
                    key={column.key}
                    className="box-table-cell box-table-head"
                    style={{ width: column.width }}
                  >
                    {column.label}
                    {isSortable && (
                      <button
                        style={{
                          border: "none",
                          background: "none",
                          padding: 0,
                          margin: 0,
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenSortMenu(e, column.key);
                        }}
                      >
                        <img
                          src={TableHeaderArrow}
                          alt="Sort"
                          style={{
                            cursor: "pointer",
                            width: "12px",
                            height: "12px",
                            opacity: sortConfig?.key === column.key ? 1 : 0.4,
                            transition: "opacity 0.2s ease",
                            marginLeft: "8px",
                            verticalAlign: "middle",
                          }}
                          title="Sort options"
                        />
                      </button>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.length > 0 ? (
              sortedData.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex + 1}
                  className={
                    onRowClick ? "clickable-row" : "box-table-body-row"
                  }
                  onClick={() => {
                    if (sortMenuAnchor || blockRowClick) return;
                    if (onRowClick) {
                      onRowClick(row);
                    }
                  }}
                >
                  {columns.map((column) => {
                    const cellValue = column.render
                      ? column.render(row)
                      : row[column.key];
                    const renderedColumnCell = column.render
                      ? column.render(row)
                      : null;

                    const isText =
                      typeof cellValue === "string" ||
                      typeof cellValue === "number";

                    let cellContent: React.ReactNode = cellValue ?? "-";

                    if (column.key === "remove") {
                      if (renderedColumnCell) {
                        cellContent = renderedColumnCell;
                      } else {
                        cellContent = (
                          <button
                            style={{
                              border: "none",
                              background: "none",
                              padding: 0,
                              margin: 0,
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onRemoveClick) {
                                onRemoveClick(row);
                            }}}
                          >
                            <img
                              src={RemoveIcon}
                              alt="Remove"
                              className="remove-icon"
                              style={{ cursor: "pointer", width: 18, height: 18 }}
                            />
                          </button>
                        );
                      }
                    } else if (isText) {
                      cellContent = (
                        <TooltipCell
                          content={cellValue}
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#403C6F",
                          }}
                        />
                      );
                    } else if (
                      (cellValue?.props?.children ||
                        cellValue?.props?.children == 0) &&
                      column.key !== "remove"
                    ) {
                      cellContent = (
                        <CustomTooltip
                          title={cellValue?.props?.children}
                          arrow
                          placement="top-start"
                        >
                          {cellValue ?? "-"}
                        </CustomTooltip>
                      );
                    }

                    return (
                      <TableCell
                        key={column.key}
                        className="box-table-cell"
                        style={{ width: column.width }}
                      >
                        {cellContent}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="no-data"
                  style={{ textAlign: "center" }}
                >
                  {noResultText || "No data found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Sort menu Popover rendered via Portal */}
      {Boolean(sortMenuAnchor) &&
        ReactDOM.createPortal(
          <Popover
            open={Boolean(sortMenuAnchor)}
            anchorEl={sortMenuAnchor}
            onClose={handleCloseSortMenu}
            container={document.body}
            disablePortal={false}
            disableScrollLock={true}
            disableAutoFocus={true}
            disableEnforceFocus={true}
            disableRestoreFocus={true}
            hideBackdrop={false}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
            }}
            sx={{
              zIndex: 9999,
            }}
            slotProps={{
              paper: {
                sx: {
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
                  zIndex: 9999,
                },
                onClick: (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  e.nativeEvent.stopImmediatePropagation();
                },
              },
              backdrop: {
                sx: {
                  backgroundColor: "transparent",
                },
              },
            }}
          >
            <button
              style={{
                border: "none",
                background: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                e.nativeEvent.stopImmediatePropagation();
              }}
            >

              <div
                style={{
                  padding: "4px 0 8px",
                  minWidth: "150px",
                }}
              >
                {/* Ascending */}
                <button
                  style={{
                    border: "none",
                    background: "none",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleSortSelection("asc", e);
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f5f5")
                  }
                  onMouseLeave={(e) => {
                    if (
                      !(
                        sortConfig?.key === sortMenuColumn &&
                        sortConfig.direction === "asc"
                      )
                    ) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >

                  <div
                    style={{
                      marginBottom: "5px",
                      padding: "8px 16px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      backgroundColor:
                        sortConfig?.key === sortMenuColumn &&
                        sortConfig.direction === "asc"
                          ? "#f0f0f0"
                          : "transparent",
                    }}
                  >
                    <input
                      type="radio"
                      checked={
                        sortConfig?.key === sortMenuColumn &&
                        sortConfig.direction === "asc"
                      }
                      readOnly
                      style={{ cursor: "pointer", pointerEvents: "none" }}
                    />
                    <span style={{ fontSize: "13px" }}>Ascending</span>
                  </div>
                </button>

                {/* Descending */}
                <button
                  style={{
                    border: "none",
                    background: "none",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleSortSelection("desc", e);
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f5f5")
                  }
                  onMouseLeave={(e) => {
                    if (
                      !(
                        sortConfig?.key === sortMenuColumn &&
                        sortConfig.direction === "desc"
                      )
                    ) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >

                  <div
                    style={{
                      padding: "8px 16px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      backgroundColor:
                        sortConfig?.key === sortMenuColumn &&
                        sortConfig.direction === "desc"
                          ? "#f0f0f0"
                          : "transparent",
                    }}
                  >
                    <input
                      type="radio"
                      checked={
                        sortConfig?.key === sortMenuColumn &&
                        sortConfig.direction === "desc"
                      }
                      readOnly
                      style={{ cursor: "pointer", pointerEvents: "none" }}
                    />
                    <span style={{ fontSize: "13px" }}>Descending</span>
                  </div>
                </button>

                {/* Clear Sort */}
                {sortConfig?.key === sortMenuColumn && (
                  <>
                    <div
                      style={{
                        height: "1px",
                        backgroundColor: "#e0e0e0",
                        margin: "4px 0",
                      }}
                    />
                    <button
                      style={{
                        border: "none",
                        background: "none",
                        padding: 0,
                        margin: 0,
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleSortSelection(null, e);
                      }}
                    >
                      <div
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "4px 16px 0px",
                        }}
                      >
                        <span
                          style={{
                            color: "#d32f2f",
                            fontSize: "13px",
                          }}
                        >
                          Clear Sort
                        </span>
                      </div>
                    </button>
                  </>
                )}
              </div>
            </button>
          </Popover>,
          document.body,
        )}
    </>
  );
};

export default MUIBoxTable;
